
import { defineComponent, onMounted, onUnmounted, ref, inject, computed } from "vue";
import swal from 'sweetalert2';
import api from '@/services/Api';
import { CouponLog, CouponLogsFilter } from "@/models/CouponLogs";
import { CouponLogStatus, CouponSystem, CouponAction } from "@/models/Enums";
import CouponLogsFilterModal from '@/views/Modals/CouponLogsFilterModal.vue';
import Tooltip from 'primevue/tooltip';
import { useToast } from 'primevue/usetoast';
import Toast from 'primevue/toast';
import { useI18n } from 'vue-i18n';


export default defineComponent({
    directives: { tooltip: Tooltip },
    components: { Toast, CouponLogsFilterModal },
    async setup() {
        const { t } = useI18n();
        const toast = useToast();
        const emitter: any = inject('emitter');
        const filter = ref<CouponLogsFilter>(new CouponLogsFilter())
        const logs = ref<CouponLog[]>([]);
        const hasNext = ref(false);
        const busy = ref(false);
        const total = ref(0);
        const totalFiltered = ref(0);
        const couponLogStatus = CouponLogStatus;
        const couponSystem = CouponSystem;
        const couponAction = CouponAction;
        const modalCouponLogsFilter = ref<InstanceType<typeof CouponLogsFilterModal>>();

        const GetCouponLogsApiCall = async () => {
            swal.fire({
                allowOutsideClick: false,
                showConfirmButton: false,
                didOpen: () => {
                    swal.showLoading();
                },
            });
            busy.value = true;
            const apiResult = await api.getCouponLogs(filter.value);
            if (apiResult.errorMessage || !apiResult.data) {
                swal.fire({
                    icon: 'error',
                    text: apiResult.errorMessage || 'No data'
                });
                return;
            }
            hasNext.value = apiResult.data.hasNext || false;
            if (filter.value.reference == null) {
                total.value = apiResult.data.total || 0;
                totalFiltered.value = apiResult.data.totalFiltered;
            }
            if (apiResult.data && logs.value && logs.value.length) {
                logs.value = [...logs.value, ...apiResult.data.rows];
            } else {
                logs.value = apiResult.data.rows;
            }
            filter.value.reference = apiResult.data.reference;
            busy.value = false;
            swal.close();
        };

        async function ListenerGetCouponLogs() {
            if (hasNext.value && !busy.value) {
                await GetCouponLogsApiCall();
            }
        }

        async function showAll() {
            filter.value = new CouponLogsFilter();
            logs.value = [];
            await GetCouponLogsApiCall();
        }

        const hasFilter = computed<boolean>(() => {
            if (filter.value.status) return true;
            if (filter.value.createdDateFrom) return true;
            if (filter.value.createdDateTo) return true;
            if (filter.value.providerId) return true;
            if (filter.value.externalSystem) return true;
            return false;
        });

        function goCouponLogsFilter() {
            modalCouponLogsFilter.value?.open(filter.value);
        }

        const sortBy = async (sortBy: string) => {
            if (filter.value.orderBy != sortBy) {
                filter.value.asc = true;
            } else {
                filter.value.asc = !filter.value.asc;
            }
            filter.value.orderBy = sortBy;
            filter.value.reference = null;
            logs.value = [];
            await GetCouponLogsApiCall();
        }

        const getArrowClass = (sort: string) => {
            if (sort == filter.value.orderBy) {
                if (filter.value.asc) {
                    return 'top-sort-arrow';
                } else {
                    return 'bottom-sort-arrow';
                }
            }
            return 'standart-sort-arrow';
        }

        async function copyAndToast(val: string) {
            const text = String(val ?? '');
            try {
                if (navigator.clipboard?.writeText) await navigator.clipboard.writeText(text);
                toast.add({ severity: 'success', detail: t('label.copied'), life: 1200 });
            } catch {
                // SKIP
            }
        }

        async function applyFilter(filterObj: CouponLogsFilter) {
            filter.value = filterObj;
            logs.value = [];
            filter.value.reference = null;
            await GetCouponLogsApiCall();
        }

        const onMountedCall = async () => {
            await GetCouponLogsApiCall();
            emitter.on('isBottom', ListenerGetCouponLogs);
        };
        onMounted(onMountedCall);

        onUnmounted(async () => {
            emitter.off('isBottom');
        });

        return {
            filter,
            logs,
            totalFiltered,
            total,
            showAll,
            hasFilter,
            goCouponLogsFilter,
            sortBy, getArrowClass,
            couponLogStatus,
            couponSystem,   
            modalCouponLogsFilter,
            copyAndToast,
            applyFilter,
            couponAction
        };
    },
});
